import React, { useState, useEffect } from "react";
import {
  ProgressBar,
  Iconography,
  IconographySizes,
  Loader,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { getValidationSummarySagaAction } from "../../../../../../../../redux/saga/validation-documentation/saga-actions";
import "./ValidationSummary.scss";

interface ValidationMetrics {
  mandatory: number; // 100% Mandatory met
  recommended: number; // 80-100% Recommended met
  bestPractices: number; // Best Practices mostly met
  optional: number; // Optional may or may not be met
}

interface EndorsementCriterion {
  label: string;
  value: string;
}

interface EndorsementLevel {
  name: string;
  criteriaLabel: string;
  criteriaGroups: EndorsementCriterion[][];
  description: string;
}

interface ValidationSummaryProps {
  integrationId?: number;
  documentationId?: number;
}

const ENDORSEMENT_LEVELS: EndorsementLevel[] = [
  {
    name: "Full Endorsement",
    criteriaLabel: "Criteria",
    criteriaGroups: [
      [
        { label: "Mandatory Score:", value: "100%" },
        { label: "Recommended Score:", value: "40% or more" },
        { label: "Best Practice Score:", value: "20% or more" },
      ],
    ],
    description:
      "The solution has met all mandatory validation requirements and demonstrates a high level of adherence to recommended and best practice guidelines. The implementation is considered fully compliant, production-ready, and aligned with expected integration standards. No corrective actions are required.",
  },
  {
    name: "Endorsement with Minor Reservations",
    criteriaLabel: "Criteria",
    criteriaGroups: [
      [
        { label: "Mandatory Score:", value: "100%" },
        {
          label: "Recommended Score:",
          value: "20% or more but less than 40%",
        },
        {
          label: "Best Practice Score:",
          value: "10% or more but less than 20%",
        },
      ],
    ],
    description:
      "The solution satisfies all mandatory requirements and achieves a good level of compliance with recommended and best practice guidelines. Minor gaps or improvement opportunities have been identified but do not present significant risk to functionality, security, reliability, or user experience. The solution is endorsed for deployment, with recommendations for future improvement.",
  },
  {
    name: "Conditional Endorsement",
    criteriaLabel: "Criteria (any one of the following)",
    criteriaGroups: [
      [
        { label: "Mandatory Score:", value: "100%" },
        { label: "Recommended Score:", value: "20% to 39%" },
        { label: "Best Practice Score:", value: "less than 10%" },
      ],
      [
        { label: "Mandatory Score:", value: "90% to 99%" },
        { label: "Recommended Score:", value: "40% or more" },
        { label: "Best Practice Score:", value: "any score" },
      ],
    ],
    description:
      "The solution demonstrates substantial compliance with validation requirements but contains notable gaps that require attention. The implementation may proceed only with documented risk acceptance and a defined remediation plan. Identified issues should be addressed within an agreed timeframe to achieve a higher endorsement level.",
  },
  {
    name: "Not Endorsed",
    criteriaLabel: "Criteria (any one of the following)",
    criteriaGroups: [
      [
        { label: "Mandatory Score:", value: "90% to 100%" },
        { label: "Recommended Score:", value: "less than 20%" },
        { label: "Best Practice Score:", value: "any score" },
      ],
      [
        { label: "Mandatory Score:", value: "less than 90%" },
        { label: "Recommended Score:", value: "any score" },
        { label: "Best Practice Score:", value: "any score" },
      ],
    ],
    description:
      "The solution does not meet the minimum validation standards required for endorsement. Deficiencies in mandatory and/or recommended requirements introduce unacceptable operational, security, reliability, maintainability, or supportability risks. Deployment is not recommended until the identified gaps have been addressed and the solution has successfully completed revalidation.",
  },
];

const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  integrationId,
  documentationId,
}) => {
  const dispatch = useAppDispatch();
  const { summaryData, status } = useAppSelector(
    (state) => state.validation_documentation
  );
  const [isAcceptanceLevelsExpanded, setIsAcceptanceLevelsExpanded] =
    useState(false);

  // Fetch summary data when integrationId and documentationId are provided
  useEffect(() => {
    if (integrationId && documentationId) {
      dispatch(getValidationSummarySagaAction(integrationId, documentationId));
    }
  }, [integrationId, documentationId, dispatch]);

  // Use API data if available, otherwise use defaults
  const overallScore = summaryData?.overall_score ?? 0;
  const totalValidationScore = summaryData?.total_validation_score ?? 0;
  const endorsementLevel = summaryData?.endorsement_level ?? "Not Endorsed";

  const validationMetrics: ValidationMetrics = {
    mandatory: summaryData?.scores.mandatory ?? 0,
    recommended: summaryData?.scores.recommended ?? 0,
    bestPractices: summaryData?.scores.bestPractice ?? 0,
    optional: summaryData?.scores.optional ?? 0,
  };

  const showWarning = endorsementLevel === "Not Endorsed";

  // Check if there's a "No active validation found" error
  const isNoActiveValidation =
    status.summaryError &&
    status.summaryError.toLowerCase().includes("no active validation found");

  // Check if documentationId is missing (new version not yet saved)
  const isNoDocumentationId = !documentationId;

  // Check if summaryData matches the current documentationId (compare as numbers; API may return string)
  const isSummaryDataValid =
    summaryData &&
    documentationId != null &&
    Number(summaryData.documentation_id) === Number(documentationId);

  return (
    <div className="validation-summary">
      {/* Show message to save first if no documentationId */}
      {isNoDocumentationId && (
        <div className="save-first-message">
          Please save your validation first to view the summary.
        </div>
      )}

      {/* Show loading state */}
      {!isNoDocumentationId && status.summaryLoading && (
        <div className="loading-summary-data">
          <Loader />
          Loading summary data...
        </div>
      )}

      {/* Show "No active validation found" message */}
      {!isNoDocumentationId && !status.summaryLoading && isNoActiveValidation && (
        <div className="no-active-validation-message">
          No active validation found
        </div>
      )}

      {/* Show error state (if not "No active validation found") */}
      {!isNoDocumentationId &&
        !status.summaryLoading &&
        status.summaryError &&
        !isNoActiveValidation && (
          <div className="error-summary-data">Error: {status.summaryError}</div>
        )}

      {/* Summary Section - only show if we have data that matches current documentationId */}
      {!isNoDocumentationId && !status.summaryLoading && !status.summaryError && isSummaryDataValid && (
        <div className="summary-section-container">
          <div className="summary-section">
            <p className="summary-intro">
              The following validation results demonstrate compliance with the
              key requirements, with minor gaps identified for follow-up action,
              if any.
            </p>

            <div className="validation-metrics">
              <div className="metric-item">
                <ProgressBar
                  progress={validationMetrics.mandatory}
                  progressOptions={{
                    labelLeft: "Mandatory",
                    labelRight: "",
                  }}
                  aria-label="Mandatory requirements progress"
                />
                <span className="metric-percentage">
                  {validationMetrics.mandatory}%
                </span>
              </div>

              <div className="metric-item">
                <ProgressBar
                  progress={validationMetrics.recommended}
                  progressOptions={{
                    labelLeft: "Recommended",
                    labelRight: "",
                  }}
                  aria-label="Recommended requirements progress"
                />
                <span className="metric-percentage">
                  {validationMetrics.recommended}%
                </span>
              </div>

              <div className="metric-item">
                <ProgressBar
                  progress={validationMetrics.bestPractices}
                  progressOptions={{
                    labelLeft: "Best Practices",
                    labelRight: "",
                  }}
                  aria-label="Best Practices progress"
                />
                <span className="metric-percentage">
                  {validationMetrics.bestPractices}%
                </span>
              </div>

              <div className="metric-item" style={{ borderBottom: "none" }}>
                <ProgressBar
                  progress={validationMetrics.optional}
                  progressOptions={{
                    labelLeft: "Optional",
                    labelRight: "",
                  }}
                  aria-label="Optional requirements progress"
                />
                <span className="metric-percentage">
                  {validationMetrics.optional}%
                </span>
              </div>
            </div>

            {showWarning && (
              <div className="warning-banner">
                <Iconography
                  icon="ban"
                  size={IconographySizes.Medium}
                  className="warning-icon"
                />
                <span className="warning-text">
                  The solution does not meet the minimum criteria for deployment
                  and is not suitable for use.
                </span>
              </div>
            )}
          </div>

          {/* Overall Feedback Section */}
          <div className="overall-feedback-section">
            <h3 className="section-title">Overall feedback</h3>
            <div className="feedback-content">
              <div className="score-display">
                <span className="score-label">Overall score total:</span>
                <span className="score-value">{overallScore} / {totalValidationScore}</span>
              </div>
              <div className="endorsement-display">
                <span className="endorsement-label">Endorsement Level:</span>
                <span className="endorsement-value">{endorsementLevel}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Acceptance Levels Section - Always show */}
      <div className="acceptance-levels-section">
        <div className="section-header">
          <h3 className="section-title">Validation acceptance levels</h3>
          <button
            className="expand-toggle"
            onClick={() =>
              setIsAcceptanceLevelsExpanded(!isAcceptanceLevelsExpanded)
            }
          >
            {isAcceptanceLevelsExpanded ? "View less" : "View more"}
            <Iconography
              icon={isAcceptanceLevelsExpanded ? "chevronUp" : "chevronDown"}
              size={IconographySizes.Small}
            />
          </button>
        </div>

        {isAcceptanceLevelsExpanded && (
          <div className="acceptance-levels-content">
            <p className="acceptance-levels-intro">
              A formal rating that indicates how strongly a solution, product,
              or process is supported for adoption or use, usually based on the
              validation process. It is to communicate readiness, compliance,
              and suitability of the integrated solution to integrators, to make
              informed decisions.
            </p>

            <div className="endorsement-levels-list">
              {ENDORSEMENT_LEVELS.map((level) => (
                <div key={level.name} className="endorsement-level-item">
                  <h4 className="level-name">{level.name}</h4>

                  <div className="criteria-label">{level.criteriaLabel}</div>

                  <div className="level-criteria">
                    {level.criteriaGroups.map((group, groupIndex) => (
                      <React.Fragment key={`${level.name}-group-${groupIndex}`}>
                        {groupIndex > 0 ? (
                          <div className="criteria-or">OR</div>
                        ) : null}
                        <ul className="criteria-list">
                          {group.map((criterion) => (
                            <li
                              key={`${level.name}-${criterion.label}-${criterion.value}`}
                              className="criterion-item"
                            >
                              <span className="criterion-text">
                                {criterion.label}{" "}
                                <strong>{criterion.value}</strong>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </React.Fragment>
                    ))}
                  </div>

                  <p className="level-description">
                    <strong>Description</strong> {level.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationSummary;
